import { Container, Typography, Box, Grid } from '@mui/material';
import { motion } from 'framer-motion';
import { Surface } from '../components/primitives';

const MotionSurface = motion(Surface);
const MotionBox = motion(Box);

const containerVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, staggerChildren: 0.08 } },
};
const itemVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0 },
};

const values = [
    { title: 'Transparency', description: 'Clear, accessible, and verifiable data. Users make informed decisions with confidence.' },
    { title: 'Non-Partisanship', description: 'Neutral and objective. Unbiased facts with no political interference.' },
    { title: 'Accuracy', description: 'Precise, reliable data held to rigorous standards.' },
    { title: 'Empowerment', description: 'Tools that unlock opportunities and reveal financial trends shaping politics.' },
    { title: 'Democratization', description: 'Accessible congressional trade data enriched with essential context.' },
    { title: 'Innovation', description: 'Cutting-edge technology and analysis to continuously improve insights.' },
];

const AboutUs = () => (
    <Container maxWidth="lg" sx={{ pt: 6, pb: 8 }}>
        <MotionBox
            initial="hidden"
            animate="visible"
            variants={containerVariants}
        >
            <MotionBox variants={itemVariants} sx={{ mb: 5 }}>
                <Typography variant="h1" sx={{ color: 'text.primary', mb: 1 }}>
                    About Capitol Gains
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Transparent insight into how U.S. Congress members trade the market.
                </Typography>
            </MotionBox>

            <MotionSurface variants={itemVariants} padding={{ xs: 3, md: 5 }} sx={{ mb: 4 }}>
                <Typography variant="body1" sx={{ color: 'text.primary', mb: 2.5, lineHeight: 1.7 }}>
                    Capitol Gains is your go-to platform for detailed insights into the stock trading
                    activities of U.S. Congress members. We present a comprehensive view of congressional
                    trades, enriched with critical context — stock values at the time of each transaction,
                    their performance over time, and the legislation members support while trading.
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2.5, lineHeight: 1.7 }}>
                    Our mission is to make this information accessible, understandable, and actionable —
                    whether you're tracking individual members, looking for patterns, or analyzing
                    returns. We turn raw disclosure data into clear analytics.
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    Monitor the financial decisions of elected officials and stay informed about their
                    potential influence on markets — with clear visuals and straightforward analytics.
                </Typography>
            </MotionSurface>

            <MotionBox variants={itemVariants} sx={{ mb: 3, mt: 6 }}>
                <Typography variant="h3" sx={{ color: 'text.primary', mb: 1 }}>
                    Our values
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    The principles that guide how we build Capitol Gains.
                </Typography>
            </MotionBox>

            <Grid container spacing={2}>
                {values.map((value, i) => (
                    <Grid item xs={12} md={6} key={i}>
                        <MotionSurface variants={itemVariants} padding={3} sx={{ height: '100%' }}>
                            <Typography variant="overline" sx={{ color: 'primary.light', display: 'block', mb: 0.5 }}>
                                {String(i + 1).padStart(2, '0')}
                            </Typography>
                            <Typography variant="h5" sx={{ color: 'text.primary', mb: 1 }}>
                                {value.title}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                                {value.description}
                            </Typography>
                        </MotionSurface>
                    </Grid>
                ))}
            </Grid>
        </MotionBox>
    </Container>
);

export default AboutUs;
